/**
 * OAuth authentication flow logic (framework-agnostic)
 */

import * as Sentry from "@sentry/react"
import { z } from "zod"
import { deleteAuthToken, getAuthToken } from "./cookie-utils"
import {
	clearOAuthTokens,
	getOAuthTokens,
	hasOAuthTokens,
	saveOAuthTokens,
} from "./token-utils"
import type { OAuthCredentials } from "./types"
import { USER_ID_KEY } from "./types"

const refreshTokensResponseSchema = z.object({
	access_token: z.string().min(1),
	refresh_token: z.string().min(1),
})

type RefreshTokensResponse = z.infer<typeof refreshTokensResponseSchema>

type RefreshTokensApiResponse = {
	status: number
	data: RefreshTokensResponse
	headers: Headers
}

type AuthApiResponse = {
	status: number
	data: unknown
	headers: Headers
}

/**
 * Fetch client shape expected by the OAuth server (Orval-generated custom-fetch mutators satisfy this).
 */
export type OAuthFetch = <T>(url: string, options: RequestInit) => Promise<T>

export type AuthFlow = {
	establishAuthSession: (userId: string, corev2Token: string) => Promise<void>
	refreshAuthTokens: () => Promise<{
		access_token: string
		refresh_token: string
	} | null>
	logout: (clientId: string) => Promise<void>
}

/**
 * Builds the auth-flow functions that need to call the OAuth server, bound to
 * the caller's fetch client — the OAuth base URL/domain differs per app (organization-admin,
 * platform-admin), so this stays a factory instead of a module-level import.
 */
export function createAuthFlow(customOAuthFetch: OAuthFetch): AuthFlow {
	/**
	 * Устанавливает JWT auth-cookie через OAuth-сервер (Domain=.${DOMAIN}),
	 * вместо старого stats-api/auth (host-only cookie только для stats-api).
	 * Используется и при первичном логине, и при рефреше токенов.
	 */
	async function establishAuthSession(
		userId: string,
		corev2Token: string,
	): Promise<void> {
		const sessionUrl = `/session?user=${encodeURIComponent(userId)}&token=${encodeURIComponent(corev2Token)}`
		await customOAuthFetch<AuthApiResponse>(sessionUrl, {
			method: "GET",
		})
	}

	/**
	 * Refresh authentication tokens with retry support for network errors
	 */
	async function refreshAuthTokens(): Promise<{
		access_token: string
		refresh_token: string
	} | null> {
		const storedTokens = getOAuthTokensFromStorage()
		const userId = getUserId()

		if (!storedTokens || !userId) {
			return null
		}

		try {
			const refreshResponse = await customOAuthFetch<RefreshTokensApiResponse>(
				"/refresh",
				{
					method: "POST",
					body: JSON.stringify({
						token: storedTokens.token || "",
						refresh: storedTokens.refreshToken || "",
					}),
				},
			)

			if (refreshResponse?.status !== 200 || !refreshResponse?.data) {
				return null
			}

			const validationResult = refreshTokensResponseSchema.safeParse(
				refreshResponse.data,
			)

			if (!validationResult.success) {
				Sentry.captureException(validationResult.error, {
					tags: { category: "auth", operation: "token_refresh_validation" },
					extra: {
						issues: validationResult.error.issues,
					},
				})
				if (process.env.NODE_ENV === "development") {
					console.error("Zod validation error:", validationResult.error.issues)
				}
				return null
			}

			const newTokens = validationResult.data

			try {
				await establishAuthSession(userId, newTokens.access_token)
			} catch (authError) {
				// Если /session запрос не удался, логируем, но не прерываем процесс
				// Токены уже обновлены, JWT будет установлен при следующем запросе
				Sentry.captureException(authError, {
					tags: { category: "auth", operation: "token_refresh_auth_call" },
				})
				if (process.env.NODE_ENV === "development") {
					console.warn("Failed to set JWT token via /session:", authError)
				}
			}

			return {
				access_token: newTokens.access_token,
				refresh_token: newTokens.refresh_token,
			}
		} catch (error) {
			console.error("Token refresh failed:", error)
			Sentry.captureException(error, {
				tags: { category: "auth", operation: "token_refresh" },
			})
			return null
		}
	}

	/**
	 * Logout — POST oauth /logout (destroys session + clears HttpOnly token cookie), then local clear.
	 */
	async function logout(clientId: string): Promise<void> {
		const tokens = getOAuthTokensFromStorage()
		const userId = getUserId()

		try {
			await customOAuthFetch("/logout", {
				method: "POST",
				body: JSON.stringify({
					token: tokens?.token ?? "",
					client_id: clientId,
					userid: userId ?? "",
				}),
			})
		} catch (error) {
			Sentry.captureException(error, {
				tags: { category: "auth", operation: "oauth_logout" },
			})
		}

		await clearAllAuthData()
		Sentry.addBreadcrumb({
			category: "auth",
			message: "User logged out",
			level: "info",
		})
	}

	return { establishAuthSession, refreshAuthTokens, logout }
}

/**
 * Check if authorization code is present in URL parameters
 * @returns authorization code if present, null otherwise
 */
export function checkAuthorizationCode(): string | null {
	if (typeof window === "undefined") {
		return null
	}

	try {
		const urlParams = new URLSearchParams(window.location.search)
		const code = urlParams.get("code")

		if (code && code.trim() !== "") {
			Sentry.addBreadcrumb({
				category: "auth",
				message: "Authorization code found in URL",
				level: "info",
			})
			return code
		}

		return null
	} catch (error) {
		Sentry.captureException(error, {
			tags: { category: "auth", operation: "check_auth_code" },
		})
		return null
	}
}

/**
 * Check if OAuth tokens exist in localStorage
 * @returns true if both tokens are present, false otherwise
 */
export function hasOAuthTokensInStorage(): boolean {
	return hasOAuthTokens()
}

/**
 * Get OAuth tokens from localStorage
 * @returns OAuthCredentials object or null if not found
 */
export function getOAuthTokensFromStorage(): OAuthCredentials | null {
	return getOAuthTokens()
}

/**
 * Extract JWT token from Authorization header
 * Handles edge case: missing or invalid header format
 * @param header - Authorization header value (e.g., "Bearer <token>")
 * @returns JWT token string or null if not found/invalid
 */
export function extractTokenFromHeader(header: string | null): string | null {
	if (!header) {
		// Edge case: header missing
		return null
	}

	// Check if header starts with "Bearer " (case-insensitive)
	const bearerPrefix = /^Bearer\s+/i
	if (!bearerPrefix.test(header)) {
		// Edge case: invalid header format
		return null
	}

	// Remove "Bearer " prefix (case-insensitive)
	const token = header.replace(bearerPrefix, "").trim()

	if (token.length === 0) {
		// Edge case: Bearer without token
		return null
	}

	return token
}

/**
 * Get user ID from localStorage
 * @returns user ID string or null if not found/unavailable
 */
function getUserId(): string | null {
	if (typeof window === "undefined") {
		return null
	}

	try {
		return localStorage.getItem(USER_ID_KEY)
	} catch {
		// Handle localStorage unavailability (private browsing mode)
		return null
	}
}

/**
 * Remove user ID from localStorage
 */
function removeUserId(): void {
	if (typeof window === "undefined") {
		return
	}

	try {
		localStorage.removeItem(USER_ID_KEY)
	} catch {
		// Handle localStorage unavailability
		// Silently fail - user_id is not critical for logout
	}
}

/**
 * Clear all authentication data (OAuth tokens and JWT token)
 */
export async function clearAllAuthData(): Promise<void> {
	clearOAuthTokens()
	removeUserId()
	await deleteAuthToken()
}

/**
 * Check if JWT token exists in cookie
 * @deprecated Use hasOAuthTokensInStorage instead
 * @returns Promise resolving to true if token exists
 */
export async function hasAuthToken(): Promise<boolean> {
	const token = await getAuthToken()
	return token !== null
}

/**
 * Save OAuth tokens after successful authentication
 * @param oauthTokens - OAuth tokens from exchange/refresh
 */
export function saveAuthenticationTokens(oauthTokens: OAuthCredentials): void {
	// Save OAuth tokens to localStorage
	saveOAuthTokens(oauthTokens)

	Sentry.addBreadcrumb({
		category: "auth",
		message: `Authentication tokens saved successfully`,
		level: "info",
	})
}
